import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { motion, PanInfo } from 'framer-motion';
import Product, { ProductProps } from './Product';
import { ProductCategory, ProductType } from '../../accessExpress.types';
import BeamTracker from './BeamTracker';
import Skeleton from 'components/Skeleton/Skeleton';
import clsx from 'clsx';
import { useResizeObserver } from 'hooks/useResizeObserver';

interface ProductsProps extends Pick<ProductProps, 'price_type'> {
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  onChangePageHistory: (page: number) => void;
  isLoading: boolean;
  products: ProductType[];
  total_pages: number;
  page?: number;
  page_size: 12 | 9 | 6 | 2;
  onProductClick: ProductProps['onClick'];
  type?: 'pagination' | 'initial';
  categoryActive?: ProductCategory | null;
}

export const getPageSize = (): 12 | 9 | 6 | 2 => {
  const width = window.innerWidth;

  if (width >= 1280) return 12; // xl:grid-cols-4, 3 rows (4 * 3)
  if (width >= 1024) return 9; // lg:grid-cols-3, 3 rows (3 * 3)
  if (width >= 640) return 6; // sm:grid-cols-2, 3 rows (2 * 3)
  return 2; // grid-cols-1, 2 rows (1 * 2)
};

const Products: React.FC<ProductsProps> = ({
  currentPage,
  setCurrentPage,
  total_pages,
  products,
  isLoading,
  page,
  page_size,
  onProductClick,
  price_type,
  onChangePageHistory,
  type = 'pagination',
  categoryActive = null,
}) => {
  const [store, setStore] = useState(() => ({
    prevPage: currentPage,
    showLoading: false,
  }));
  const [data, setData] = useState<ProductType[][]>([]);
  const [isCursorInside, setIsCursorInside] = useState<boolean>(false);
  const [dragging, setDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [duration, setDuration] = useState(0.7);

  const { ref: containerRef } = useResizeObserver({
    onBeforeResize: ({ width }) => {
      setDuration(0);
      setContainerWidth(width);
    },
    onAfterResize: () => setDuration(0.7),
  });

  useEffect(() => {
    if (type === 'pagination')
      setStore((prev) => {
        if (prev.prevPage === currentPage && isLoading)
          return { ...prev, showLoading: true };
        else return { prevPage: currentPage, showLoading: false };
      });
  }, [isLoading, currentPage, type]);

  // Handle Arrow Key Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (!isCursorInside && !data.length) return;

      if (e.key === 'ArrowLeft')
        setCurrentPage((prev) => {
          onChangePageHistory(Math.max(0, prev - 1));
          return Math.max(0, prev - 1);
        });
      else if (e.key === 'ArrowRight')
        setCurrentPage((prev) => {
          onChangePageHistory(Math.min(total_pages - 1, prev + 1));
          return Math.min(total_pages - 1, prev + 1);
        });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isCursorInside,
    setCurrentPage,
    total_pages,
    data.length,
    onChangePageHistory,
  ]);

  // Handle Drag End with Snap-to-Page
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    setDragging(false);
    const { offset, velocity } = info;
    const swipeThreshold = (containerWidth || 0) / 3.5;
    const direction = velocity.x > 0 ? -1 : 1;

    let nextPage = currentPage;

    if (Math.abs(offset.x) > swipeThreshold || Math.abs(velocity.x) > 0.5)
      nextPage = Math.min(
        Math.max(currentPage + direction, 0),
        total_pages - 1,
      );
    onChangePageHistory(nextPage);
    setCurrentPage(nextPage);
  };

  // Handle Mouse Enter/Leave to enable keyboard navigation
  const handleMouseEnter = () => setIsCursorInside(true);
  const handleMouseLeave = () => setIsCursorInside(false);

  useEffect(() => {
    let lastDelta = 0;
    let isScrolling = false;
    let animationFrameId: number | null = null;

    const handleWheel = (event: WheelEvent) => {
      if (!isCursorInside || isScrolling || !data.length) return;

      const isTrackpad =
        Math.abs(event.deltaX) > 0 || Math.abs(event.deltaY) < 15;

      // If it's a trackpad, allow natural horizontal scroll
      if (isTrackpad && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        lastDelta += event.deltaX;

        if (animationFrameId) cancelAnimationFrame(animationFrameId);

        animationFrameId = requestAnimationFrame(() => {
          const threshold = (containerWidth || 0) / 4;

          if (Math.abs(lastDelta) >= threshold) {
            const direction = lastDelta > 0 ? 1 : -1;

            setCurrentPage((prevPage) => {
              const nextPage = Math.min(
                Math.max(prevPage + direction, 0),
                total_pages - 1,
              );
              onChangePageHistory(nextPage !== prevPage ? nextPage : prevPage);
              return nextPage !== prevPage ? nextPage : prevPage;
            });

            lastDelta = 0;
            isScrolling = true;
            setTimeout(() => (isScrolling = false), 300);
          }
        });
      }

      // If mouse wheel (vertical scroll), map it to horizontal
      if (!isTrackpad && Math.abs(event.deltaY) > 0) {
        event.preventDefault(); // Prevent vertical scroll

        const direction = event.deltaY > 0 ? 1 : -1;

        setCurrentPage((prevPage) => {
          const nextPage = Math.min(
            Math.max(prevPage + direction, 0),
            total_pages - 1,
          );
          onChangePageHistory(nextPage !== prevPage ? nextPage : prevPage);
          return nextPage !== prevPage ? nextPage : prevPage;
        });

        isScrolling = true;
        setTimeout(() => (isScrolling = false), 300);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [
    isCursorInside,
    setCurrentPage,
    onChangePageHistory,
    total_pages,
    data.length,
    containerWidth,
  ]);

  useEffect(() => {
    if (type === 'pagination') {
      if (page === undefined || !products.length) return;

      setData((prev) => {
        if (prev[page - 1] === products) return prev;

        const updatedData = [...prev];
        updatedData[page - 1] = products;

        return updatedData;
      });
    }
  }, [products, total_pages, page, type]);

  useEffect(() => {
    if (type === 'initial') {
      setData(() => {
        const updatedData = [];
        for (let i = 0; i < products.length; i += page_size) {
          updatedData.push(products.slice(i, i + page_size));
        }
        return updatedData;
      });
    }
  }, [products, total_pages, page_size, type]);

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="flex flex-1 flex-col justify-between"
    >
      <div ref={containerRef} className="relative w-full">
        <motion.div
          className="flex"
          {...(data.length && { drag: 'x' })}
          dragConstraints={{
            left: -(containerWidth || 0) * (total_pages - 1),
            right: 0,
          }}
          dragElastic={0.1}
          dragMomentum={false}
          onDragStart={() => setDragging(true)}
          onDragEnd={handleDragEnd}
          animate={{
            x: -currentPage * (containerWidth || 0),
          }}
          transition={{
            duration: duration,
            type: 'spring',
            mass: 1,
            stiffness: 45,
            damping: 15,
          }}
        >
          {[...Array(total_pages === 0 ? 1 : total_pages)].map((_, index) => (
            <motion.div
              key={index}
              className={clsx(
                'grid w-full shrink-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
                { 'pointer-events-none': dragging },
              )}
              initial={false}
              animate={{ opacity: currentPage === index ? 1 : 0 }}
              transition={{ duration: 0.7, ease: 'easeInOut' }}
            >
              {data[index] &&
              data[index]?.length !== 0 &&
              !store.showLoading ? (
                Array.from({ length: page_size }).map((_, i) =>
                  data[index][i] ? (
                    <Product
                      price_type={price_type}
                      key={i}
                      product={data[index][i]}
                      onClick={onProductClick}
                      active={data[index][i].category === categoryActive}
                    />
                  ) : (
                    <div className="h-[229px] w-full" key={i}></div>
                  ),
                )
              ) : (
                <></>
              )}

              {(isLoading && !data[index]) || (isLoading && store.showLoading)
                ? Array.from({ length: page_size }).map((_, i) => (
                    <Skeleton className="h-[229px] w-full !rounded" key={i} />
                  ))
                : null}
            </motion.div>
          ))}
        </motion.div>
      </div>
      {data.length ? (
        <BeamTracker
          currentPage={currentPage}
          onChangePageHistory={onChangePageHistory}
          setCurrentPage={setCurrentPage}
          totalPage={total_pages}
        />
      ) : (
        isLoading && (
          <div className="mt-3.5 flex h-[13px] items-center">
            <div className="h-[3px] w-full bg-neutral-700/50" />
          </div>
        )
      )}
    </div>
  );
};

export default Products;
